import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { addRolesPermission, getRolesItem } from "../../../store/roles/slice";
import { useAppSelector, useAppDispatch } from "../../../store/index";
import { usePermissions } from "../../../helpers/usePermission";
import {
  NotificationTypes,
  showNotification,
} from "../../../store/notifications/slice";

import arrowIcon from "../../../assets/icons/sidebar/arrow-down.svg";

const Sidebar: React.FC = () => {
  const [activeModule, setActiveModule] = useState<string>("");
  const [activeSubsegment, setActiveSubsegment] = useState<string>("");
  const [modules, setModules] = useState<string[]>([]);
  const [subModules, setSubModules] = useState<{ [key: string]: string[] }>();
  const [permissionFetching, setPermissionFetching] = useState<boolean>(false);
  const lastPermissionWarningAt = useRef(0);

  const activePermissions = usePermissions("roles_list");
  const isPermissionUpdateDenied =
    activePermissions !== null && !activePermissions?.includes("update");

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const allModules = useAppSelector((store) => store.settings.modules);
  const activeItem = useAppSelector((store) => store.roles.item);
  const isSelectedSystemRole =
    activeItem?.isSystem === true ||
    ["player", "admin"].includes(activeItem?.title?.toLowerCase() ?? "");

  useEffect(() => {
    if (allModules) {
      setModules(Object.keys(allModules as object).map((el) => el));
    }
  }, [allModules]);

  useEffect(() => {
    setActiveModule(modules[0]);
    // @ts-ignore
    if (allModules) setSubModules(allModules[modules[0]]);
  }, [modules, allModules]);

  const getIsCheckedClass = (
    segment: string,
    action: string,
    module: string,
    subSegment?: string,
  ) => {
    let resultClass = undefined;
    if (activeItem?.permissions) {
      Object.keys(activeItem.permissions).forEach((el) => {
        const result = subSegment
          ? // @ts-ignore
            activeItem.permissions[
              `${module?.toLowerCase()}_${segment?.toLowerCase()}_${subSegment?.toLowerCase()}`
            ]
          : // @ts-ignore
            activeItem.permissions[
              `${module?.toLowerCase()}_${segment?.toLowerCase()}`
            ];
        // @ts-ignore
        const subItems = subModules[segment];
        if (subItems.length > 0 && !subSegment) {
          let loopResult = 0;
          subItems.forEach((el) => {
            if (
              // @ts-ignore
              activeItem.permissions[
                `${module?.toLowerCase()}_${segment?.toLowerCase()}_${el?.toLowerCase()}`
              ]?.includes(action)
            ) {
              loopResult = loopResult + 1;
            }
          });
          resultClass = loopResult === subItems.length ? "checked" : undefined;
        } else {
          if (result && result.includes(action)) {
            resultClass = "checked";
          } else {
            resultClass = undefined;
          }
        }
      });
    }
    return resultClass;
  };

  const handleTogglePermission = async (
    segment: string,
    action: string,
    value: number,
    subSegment?: string,
  ) => {
    setPermissionFetching(true);
    if (!subSegment) {
      // @ts-ignore
      const subItems = subModules[segment];
      if (subItems && subItems.length > 0) {
        subItems.forEach(async (el) => {
          try {
            await dispatch(
              addRolesPermission({
                actionName: action,
                actionValue: value,
                section1: activeModule,
                section2: segment,
                section3: el,
                role: activeItem?.title ?? "",
              }),
            );
            await dispatch(getRolesItem(activeItem?.id ?? 0));
          } catch {
            console.log("error");
          }
        });
      } else {
        try {
          await dispatch(
            addRolesPermission({
              actionName: action,
              actionValue: value,
              section1: activeModule,
              section2: segment,
              section3: subSegment,
              role: activeItem?.title ?? "",
            }),
          );
          await dispatch(getRolesItem(activeItem?.id ?? 0));
        } catch {
          console.log("error");
        }
      }
    } else {
      try {
        await dispatch(
          addRolesPermission({
            actionName: action,
            actionValue: value,
            section1: activeModule,
            section2: segment,
            section3: subSegment,
            role: activeItem?.title ?? "",
          }),
        );
        await dispatch(getRolesItem(activeItem?.id ?? 0));
      } catch {
        console.log("error");
      }
    }
    setPermissionFetching(false);
  };

  const renderInputs = (segment: string) => {
    return (
      <div className="segment-inputs">
        <div className="role-input">
          <div
            onClick={() => {
              if (
                permissionFetching ||
                (!activePermissions?.includes("update") &&
                  activePermissions !== null)
              ) {
                return;
              }
              if (!!getIsCheckedClass(segment, "create", activeModule)) {
                handleTogglePermission(segment, "create", 0);
              } else {
                handleTogglePermission(segment, "create", 1);
              }
            }}
            className={`role-input--control ${getIsCheckedClass(segment, "create", activeModule)} ${permissionFetching || (!activePermissions?.includes("update") && activePermissions !== null) ? "disabled" : ""}`}
          />
        </div>
        <div className="role-input">
          <div
            onClick={() => {
              if (
                permissionFetching ||
                (!activePermissions?.includes("update") &&
                  activePermissions !== null)
              ) {
                return;
              }
              if (!!getIsCheckedClass(segment, "read", activeModule)) {
                handleTogglePermission(segment, "read", 0);
              } else {
                handleTogglePermission(segment, "read", 1);
              }
            }}
            className={`role-input--control ${getIsCheckedClass(segment, "read", activeModule)} ${permissionFetching || (!activePermissions?.includes("update") && activePermissions !== null) ? "disabled" : ""}`}
          />
        </div>
        <div className="role-input">
          <div
            onClick={() => {
              if (
                permissionFetching ||
                (!activePermissions?.includes("update") &&
                  activePermissions !== null)
              ) {
                return;
              }
              if (!!getIsCheckedClass(segment, "update", activeModule)) {
                handleTogglePermission(segment, "update", 0);
              } else {
                handleTogglePermission(segment, "update", 1);
              }
            }}
            className={`role-input--control ${getIsCheckedClass(segment, "update", activeModule)} ${permissionFetching || (!activePermissions?.includes("update") && activePermissions !== null) ? "disabled" : ""}`}
          />
        </div>
        <div className="role-input">
          <div
            onClick={() => {
              if (
                permissionFetching ||
                (!activePermissions?.includes("update") &&
                  activePermissions !== null)
              ) {
                return;
              }
              if (!!getIsCheckedClass(segment, "delete", activeModule)) {
                handleTogglePermission(segment, "delete", 0);
              } else {
                handleTogglePermission(segment, "delete", 1);
              }
            }}
            className={`role-input--control ${getIsCheckedClass(segment, "delete", activeModule)} ${permissionFetching || (!activePermissions?.includes("update") && activePermissions !== null) ? "disabled" : ""}`}
          />
        </div>
        <div className="role-input">
          <div
            onClick={() => {
              if (
                permissionFetching ||
                (!activePermissions?.includes("update") &&
                  activePermissions !== null)
              ) {
                return;
              }
              if (!!getIsCheckedClass(segment, "export", activeModule)) {
                handleTogglePermission(segment, "export", 0);
              } else {
                handleTogglePermission(segment, "export", 1);
              }
            }}
            className={`role-input--control ${getIsCheckedClass(segment, "export", activeModule)} ${permissionFetching || (!activePermissions?.includes("update") && activePermissions !== null) ? "disabled" : ""}`}
          />
        </div>
        <div className="role-input">
          <div
            onClick={() => {
              if (
                permissionFetching ||
                (!activePermissions?.includes("update") &&
                  activePermissions !== null)
              ) {
                return;
              }
              if (
                !!getIsCheckedClass(segment, "sensitive_data", activeModule)
              ) {
                handleTogglePermission(segment, "sensitive_data", 0);
              } else {
                handleTogglePermission(segment, "sensitive_data", 1);
              }
            }}
            className={`role-input--control ${getIsCheckedClass(segment, "sensitive_data", activeModule)} ${permissionFetching || (!activePermissions?.includes("update") && activePermissions !== null) ? "disabled" : ""}`}
          />
        </div>
      </div>
    );
  };
  const renderInputsSubsegment = (segment: string, subSegment: string) => {
    return (
      <div className="subsegment-inputs">
        <div className="role-input">
          <div
            onClick={() => {
              if (
                permissionFetching ||
                (!activePermissions?.includes("update") &&
                  activePermissions !== null)
              ) {
                return;
              }
              if (
                !!getIsCheckedClass(segment, "create", activeModule, subSegment)
              ) {
                handleTogglePermission(segment, "create", 0, subSegment);
              } else {
                handleTogglePermission(segment, "create", 1, subSegment);
              }
            }}
            className={`role-input--control ${getIsCheckedClass(segment, "create", activeModule, subSegment)} ${permissionFetching || (!activePermissions?.includes("update") && activePermissions !== null) ? "disabled" : ""}`}
          />
        </div>
        <div className="role-input">
          <div
            onClick={() => {
              if (
                permissionFetching ||
                (!activePermissions?.includes("update") &&
                  activePermissions !== null)
              ) {
                return;
              }
              if (
                !!getIsCheckedClass(segment, "read", activeModule, subSegment)
              ) {
                handleTogglePermission(segment, "read", 0, subSegment);
              } else {
                handleTogglePermission(segment, "read", 1, subSegment);
              }
            }}
            className={`role-input--control ${getIsCheckedClass(segment, "read", activeModule, subSegment)} ${permissionFetching || (!activePermissions?.includes("update") && activePermissions !== null) ? "disabled" : ""}`}
          />
        </div>
        <div className="role-input">
          <div
            onClick={() => {
              if (
                permissionFetching ||
                (!activePermissions?.includes("update") &&
                  activePermissions !== null)
              ) {
                return;
              }
              if (
                !!getIsCheckedClass(segment, "update", activeModule, subSegment)
              ) {
                handleTogglePermission(segment, "update", 0, subSegment);
              } else {
                handleTogglePermission(segment, "update", 1, subSegment);
              }
            }}
            className={`role-input--control ${getIsCheckedClass(segment, "update", activeModule, subSegment)} ${permissionFetching || (!activePermissions?.includes("update") && activePermissions !== null) ? "disabled" : ""}`}
          />
        </div>
        <div className="role-input">
          <div
            onClick={() => {
              if (
                permissionFetching ||
                (!activePermissions?.includes("update") &&
                  activePermissions !== null)
              ) {
                return;
              }
              if (
                !!getIsCheckedClass(segment, "delete", activeModule, subSegment)
              ) {
                handleTogglePermission(segment, "delete", 0, subSegment);
              } else {
                handleTogglePermission(segment, "delete", 1, subSegment);
              }
            }}
            className={`role-input--control ${getIsCheckedClass(segment, "delete", activeModule, subSegment)} ${permissionFetching || (!activePermissions?.includes("update") && activePermissions !== null) ? "disabled" : ""}`}
          />
        </div>
        <div className="role-input">
          <div
            onClick={() => {
              if (
                permissionFetching ||
                (!activePermissions?.includes("update") &&
                  activePermissions !== null)
              ) {
                return;
              }
              if (
                !!getIsCheckedClass(segment, "export", activeModule, subSegment)
              ) {
                handleTogglePermission(segment, "export", 0, subSegment);
              } else {
                handleTogglePermission(segment, "export", 1, subSegment);
              }
            }}
            className={`role-input--control ${getIsCheckedClass(segment, "export", activeModule, subSegment)} ${permissionFetching || (!activePermissions?.includes("update") && activePermissions !== null) ? "disabled" : ""}`}
          />
        </div>
        <div className="role-input">
          <div
            onClick={() => {
              if (
                permissionFetching ||
                (!activePermissions?.includes("update") &&
                  activePermissions !== null)
              ) {
                return;
              }
              if (
                !!getIsCheckedClass(
                  segment,
                  "sensitive_data",
                  activeModule,
                  subSegment,
                )
              ) {
                handleTogglePermission(
                  segment,
                  "sensitive_data",
                  0,
                  subSegment,
                );
              } else {
                handleTogglePermission(
                  segment,
                  "sensitive_data",
                  1,
                  subSegment,
                );
              }
            }}
            className={`role-input--control ${getIsCheckedClass(segment, "sensitive_data", activeModule, subSegment)} ${permissionFetching || (!activePermissions?.includes("update") && activePermissions !== null) ? "disabled" : ""}`}
          />
        </div>
      </div>
    );
  };

  const handleDisabledCheckboxClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    const target = event.target as HTMLElement;
    const checkbox = target.closest(".role-input--control");
    const isDeniedCheckbox =
      checkbox?.classList.contains("disabled") && isPermissionUpdateDenied;

    if (!checkbox || (!isSelectedSystemRole && !isDeniedCheckbox)) {
      return;
    }

    event.stopPropagation();

    const now = Date.now();
    if (now - lastPermissionWarningAt.current < 3000) return;
    lastPermissionWarningAt.current = now;

    dispatch(
      showNotification({
        title: t("warning"),
        message: isSelectedSystemRole
          ? "Permissions for system roles cannot be edited."
          : "You don't have permission to edit role permissions.",
        type: NotificationTypes.warning,
      }),
    );
  };

  return (
    <div
      className={`roles-table-wrapper ${
        permissionFetching ? "roles-table-wrapper--permission-fetching" : ""
      }`}
      onClickCapture={handleDisabledCheckboxClick}
    >
      <div className="roles-table-header">
        <div className="item">{t("mainSegment")}</div>
        <div className="item">{t("subSegment")}</div>
        <div className="item">{t("create")}</div>
        <div className="item">{t("read")}</div>
        <div className="item">{t("update")}</div>
        <div className="item">{t("delete")}</div>
        <div className="item">{t("export")}</div>
        <div className="item">{t("readUserContacts")}</div>
      </div>
      <div className="roles-table-body">
        <div className="item permissions">
          {modules.map((el) => (
            <div
              onClick={() => {
                setActiveModule(el);
                // @ts-ignore
                setSubModules(allModules[el]);
              }}
              className={`permission ${activeModule === el ? "active" : ""}`}
              key={el}
            >
              {el}
            </div>
          ))}
        </div>
        <div className="item segments">
          {subModules &&
            Object.keys(subModules).map((el) => (
              <div className="segment" key={el}>
                <div className="segment-main-row">
                  <div
                    className={`row ${activeSubsegment === el ? "opened" : ""}`}
                    onClick={() => {
                      if (activeSubsegment === el) {
                        setActiveSubsegment("");
                      } else {
                        setActiveSubsegment(el);
                      }
                    }}
                  >
                    <span>{el}</span>
                    {subModules[el].length > 0 ? (
                      <img src={arrowIcon} alt="" />
                    ) : null}
                  </div>
                  {renderInputs(el)}
                </div>
                {subModules[el].length > 0 ? (
                  <div
                    className={`subsegments ${activeSubsegment === el ? "opened" : ""}`}
                  >
                    {subModules[el].map((item) => (
                      <div className="subsegment" key={item}>
                        <div className="subsegment-row">{item}</div>
                        {renderInputsSubsegment(el, item)}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
